import React, { useState } from 'react';
import { useToast } from '../ui/Toast';
import { useNotificationAnalytics } from '../../hooks/useAnalytics';
import { STORAGE_KEYS } from '../../constants';
import './NotificationSettings.css';

/**
 * Notification Settings Component
 * Configuration for notification preferences and settings
 */
const NotificationSettings = () => {
  const { showToast } = useToast();
  const { data: notificationData } = useNotificationAnalytics('day');
  
  // Get admin email and create safe key
  const getAdminEmailKey = () => {
    const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
    return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
  };

  // Load preferences from localStorage
  const loadPreference = (key, defaultValue) => {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return stored === 'true';
  };

  const loadNumberPreference = (key, defaultValue) => {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    const num = parseInt(stored);
    return isNaN(num) ? defaultValue : num;
  };

  const emailKey = getAdminEmailKey();
  
  const [emailNotifications, setEmailNotifications] = useState(
    loadPreference(`${emailKey}_notifications_email`, true)
  );
  const [pushNotifications, setPushNotifications] = useState(
    loadPreference(`${emailKey}_notifications_push`, true)
  );
  const [failedNotificationsAlert, setFailedNotificationsAlert] = useState(
    loadPreference(`${emailKey}_notifications_failed_alert`, true)
  );
  const [lowDeliveryRateAlert, setLowDeliveryRateAlert] = useState(
    loadPreference(`${emailKey}_notifications_delivery_alert`, true)
  );
  const [alertThreshold, setAlertThreshold] = useState(
    loadNumberPreference(`${emailKey}_notifications_threshold`, 50)
  );

  const savePreference = (key, value) => {
    localStorage.setItem(key, value.toString());
  };

  const handleEmailNotificationsChange = (value) => {
    setEmailNotifications(value);
    savePreference(`${emailKey}_notifications_email`, value);
    showToast(`Email notifications ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  const handlePushNotificationsChange = (value) => {
    setPushNotifications(value);
    savePreference(`${emailKey}_notifications_push`, value);
    showToast(`Push notifications ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  const handleFailedNotificationsAlertChange = (value) => {
    setFailedNotificationsAlert(value);
    savePreference(`${emailKey}_notifications_failed_alert`, value);
    showToast(`Failed notifications alert ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  const handleLowDeliveryRateAlertChange = (value) => {
    setLowDeliveryRateAlert(value);
    savePreference(`${emailKey}_notifications_delivery_alert`, value);
    showToast(`Low delivery rate alert ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  const handleAlertThresholdChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 1000) {
      setAlertThreshold(numValue);
      savePreference(`${emailKey}_notifications_threshold`, numValue);
      showToast(`Alert threshold set to ${numValue}`, 'success');
    }
  };

  return (
    <div className="notification-settings admin-card">
      <h3 className="section-title">
        <i className="bi bi-bell icon-brand" style={{ marginRight: 8 }}></i>
        Notification Settings
      </h3>

      {/* Current Notification Stats */}
      {notificationData && (
        <div className="notification-stats">
          <div className="stat-item">
            <span className="stat-label">Total Notifications</span>
            <span className="stat-value">{notificationData.totalNotifications?.toLocaleString() || '0'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Delivery Rate</span>
            <span className="stat-value">{notificationData.deliveryRate?.toFixed(1) || '0'}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Failed</span>
            <span className="stat-value error">{notificationData.failedNotifications?.toLocaleString() || '0'}</span>
          </div>
        </div>
      )}

      <div className="settings-group">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Email Notifications</h4>
            <p>Receive email alerts for important events</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => handleEmailNotificationsChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Push Notifications</h4>
            <p>Receive browser push notifications</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={pushNotifications}
              onChange={(e) => handlePushNotificationsChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Failed Notifications Alert</h4>
            <p>Alert when failed notifications exceed threshold</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={failedNotificationsAlert}
              onChange={(e) => handleFailedNotificationsAlertChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Low Delivery Rate Alert</h4>
            <p>Alert when delivery rate drops below 85%</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={lowDeliveryRateAlert}
              onChange={(e) => handleLowDeliveryRateAlertChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Alert Threshold</h4>
            <p>Minimum number of failed notifications to trigger alert</p>
          </div>
          <div className="input-group">
            <input
              type="number"
              min="0"
              max="1000"
              value={alertThreshold}
              onChange={(e) => handleAlertThresholdChange(e.target.value)}
              className="number-input"
            />
            <span className="input-suffix">notifications</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
